import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const frontmatter = {
  "title": "Calidad",
  "lead": "La educación de buena calidad no puede ser equiparada, o reducida, a los resultados del aprendizaje. Demasiados niños no disponen de maestros suficientemente capacitados, una buena infraestructura escolar o un entorno de aprendizaje seguro y no violento."
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    h2: "h2",
    li: "li",
    p: "p",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  }, {Figure, HighlightByIndex, IndicatorReference, QualityFramework, RelatedMaterial, ScrollyArea, ScrollySection, ScrollySticky, Tooltip} = _components;
  if (!Figure) _missingMdxReference("Figure", true);
  if (!HighlightByIndex) _missingMdxReference("HighlightByIndex", true);
  if (!IndicatorReference) _missingMdxReference("IndicatorReference", true);
  if (!QualityFramework) _missingMdxReference("QualityFramework", true);
  if (!RelatedMaterial) _missingMdxReference("RelatedMaterial", true);
  if (!ScrollyArea) _missingMdxReference("ScrollyArea", true);
  if (!ScrollySection) _missingMdxReference("ScrollySection", true);
  if (!ScrollySticky) _missingMdxReference("ScrollySticky", true);
  if (!Tooltip) _missingMdxReference("Tooltip", true);
  return _jsxs(_Fragment, {
    children: [_jsx(Figure, {
      id: "HERO_QUALITY"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "La educación de buena calidad no puede ser equiparada, o reducida, a los resultados del aprendizaje. Demasiados niños no disponen de maestros suficientemente capacitados, una buena infraestructura escolar o un entorno de aprendizaje seguro y no violento."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "Es difícil realizar un seguimiento de la educación de buena calidad porque cada país la entiende y la define de manera diferente. Aparte de los resultados del aprendizaje, no hay muchos indicadores para evaluar la calidad en el objetivo mundial de educación, el ODS 4."
    }), "\n", _jsx(_components.h2, {
      id: "docentes",
      children: "Docentes"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.c.2",
      children: [_jsx(_components.p, {
        children: "Los docentes son los factores determinantes de la calidad de la educación. Sin embargo, después del año 2000, en el marco del esfuerzo por lograr la educación primaria universal, muchos más alumnos ingresaron a las aulas, pero la contratación de maestros no fue correlativa. Como resultado de ello, algunas aulas están abarrotadas, y la calidad de la educación se ve afectada."
      }), _jsx(QualityFramework, {
        highlighted: "teachers",
        description: "Más información sobre el marco para la calidad de la educación"
      }), _jsx(_components.p, {
        children: "Los siguientes gráficos muestran la proporción de alumnos por docente en la escuela. En algunos países de África Subsahariana, si bien la proporción ha mejorado desde 2000, sigue siendo peor que en la década de 1980."
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "PTR"
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["En ", _jsx(HighlightByIndex, {
              index: "0",
              children: "África Subsahariana"
            }), ", tras la campaña emprendida entre 1990 y 2000 para hacer gratuita la escuela primaria, la llegada de los niños a las aulas fue más rápida que la contratación de docentes por parte de los países. Esto condujo a un incremento de la proporción de alumnos por docente, a menudo bastante drástico."]
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["En ", _jsx(HighlightByIndex, {
              index: "6",
              children: "América del Norte y Europa"
            }), ", mientras tanto, la proporción se ha mantenido constante en unos 14 alumnos por docente desde 2005."]
          })
        })]
      })]
    }), "\n", _jsx(_components.p, {
      children: "La enseñanza es una tarea difícil, y los docentes que no están bien preparados entran en el aula con un grave déficit. Los países miden su grado de preparación de dos formas principales, a saber:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["En primer lugar, si están ", _jsx(_components.strong, {
          children: "cualificados"
        }), ", lo que indica si poseen las credenciales académicas esperadas de acuerdo con las normas nacionales."]
      }), "\n", _jsxs(_components.li, {
        children: ["En segundo lugar, si están ", _jsx(_components.strong, {
          children: "capacitados"
        }), ", es decir que se evalúa si, independientemente de su cualificación, recibieron una formación de docentes adecuada."]
      }), "\n"]
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.c.1",
      children: [_jsxs(_components.p, {
        children: ["Estar capacitado supone que el docente ha asistido a una formación en la que ha obtenido ", _jsx(Tooltip, {
          title: "conocimientos pedagógicos",
          content: "Knowledge on approaches, methods and techniques of teaching.",
          children: "conocimientos pedagógicos"
        }), " y ", _jsx(Tooltip, {
          title: "conocimientos profesionales",
          content: "Knowledge on, for example, the legal frameworks governing the teaching profession.",
          children: "conocimientos profesionales"
        }), ". Algunos programas también pueden abarcar el ", _jsx(Tooltip, {
          title: "conocimiento del contenido",
          content: "Knowledge of the curriculum, subject matter and use of relevant materials.",
          children: "conocimiento del contenido"
        }), ". No obstante, el tipo de formación requerida difiere según el país y la información pertinente es escasa. Debido a estas diferencias, a pesar de ser un elemento crucial de la calidad de la educación, el indicador sobre docentes capacitados [4.c.1] es uno de los menos comparables en el marco de seguimiento del ODS 4."]
      }), _jsxs(_components.p, {
        children: ["En el plano mundial, la mayoría de los docentes están cualificados, capacitados o ambas cosas. Por ejemplo, en ", _jsx(HighlightByIndex, {
          index: "4",
          children: "América Latina y el Caribe"
        }), " 80% de los docentes están capacitados."]
      }), _jsxs(_components.p, {
        children: ["En cambio, en ", _jsx(HighlightByIndex, {
          index: "0",
          children: "África Subsahariana"
        }), "el porcentaje de docentes capacitados disminuyó del 85% en 2000 al 69% en 2022."]
      }), _jsx(Figure, {
        id: "TRAINED"
      }), _jsx(_components.p, {
        children: "No hay una solución fácil para los países que carecen de docentes cualificados y capacitados. Muchos de ellos no tienen suficientes docentes que reúnan los requisitos necesarios para satisfacer la demanda, dado el escaso número de diplomados de la educación terciaria en general. Esto demuestra el grado de interconexión que pueden alcanzar los problemas dentro de un sistema educativo; así pues, los problemas en un nivel, como la baja tasa de finalización de la escuela secundaria, pueden fácilmente ejercer repercusiones en otros ámbitos, como la escasez de docentes capacitados para los estudiantes en el futuro."
      })]
    }), "\n", _jsx(_components.h2, {
      id: "infraestructura",
      children: "Infraestructura"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.a.1e",
      children: [_jsxs(_components.p, {
        children: ["Un entorno de aprendizaje apropiado debe ofrecer instalaciones adecuadas de ", _jsx(_components.strong, {
          children: "agua"
        }), " y saneamiento, especialmente para las niñas. Sin embargo, solo el 55% de las escuelas primarias de África Subsahariana, el 88% de las de Asia oriental y sudoriental y el 85% de las de Asia central y meridional disponían de servicios básicos de agua."]
      }), _jsx(QualityFramework, {
        highlighted: "material",
        description: "Más información sobre el marco para la calidad de la educación"
      })]
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.a.1f",
      children: [_jsxs(_components.p, {
        children: ["En particular, las instalaciones de ", _jsx(_components.strong, {
          children: "saneamiento"
        }), " que no ofrecen privacidad no pueden considerarse seguras, mientras que las instalaciones que no aseguran una higiene adecuada durante el período menstrual pueden impedir a las chicas asistir a la escuela. Por lo tanto, disponer de instalaciones de saneamiento separadas por sexo es una medida normativa fundamental para evitar que la matriculación tardía en la educación primaria y el abandono escolar de las chicas se generalicen con el tiempo."]
      }), _jsx(_components.p, {
        children: "El siguiente gráfico muestra que menos de la mitad de las escuelas primarias dispone de instalaciones de saneamiento separadas por sexo, no solo en África Subsahariana, sino también en países asiáticos como Afganistán, la República Democrática Popular Lao y Nepal. En Indonesia, únicamente el 64% de las escuelas primarias, el 76% de las escuelas de primer ciclo de secundaria, y el 78% de las escuelas de segundo ciclo de secundaria tienen instalaciones de saneamiento separadas por sexo."
      }), _jsx(Figure, {
        id: "SANITATION"
      })]
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.a.1a",
      children: [_jsxs(_components.p, {
        children: ["La ", _jsx(_components.strong, {
          children: "electricidad"
        }), " es una necesidad básica para un entorno de aprendizaje de calidad y, a pesar de esto, muchos países carecen de ella. En los países de bajos ingresos, el 72% de las escuelas primarias y el 57% de las escuelas de segundo ciclo de secundaria no tienen electricidad."]
      }), _jsx(_components.p, {
        children: "Como muestra el gráfico siguiente, en Nicaragua y Pakistán menos de la mitad de las escuelas primarias tiene electricidad. En las escuelas de algunos países de África Subsahariana, el acceso a ese servicio es extremadamente reducido: el 12% de las escuelas tiene electricidad en Madagascar, y tan solo el 1% en el Chad. La falta de electricidad puede hacer que la iluminación sea inadecuada, e impedir a los docentes usar ordenadores para la administración o la capacitación. No tener suministro eléctrico significa que no hay ventilación, refrigeración o calefacción, lo que puede tener como consecuencia que las aulas sean inapropiadas para la enseñanza y el aprendizaje."
      })]
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.a.1b",
      children: [_jsxs(_components.p, {
        children: ["Ello también imposibilita el uso de tecnología e ", _jsx(_components.strong, {
          children: "Internet"
        }), ". Sin embargo, la electricidad no es el único factor que frena la ampliación de Internet. En los países de ingresos medianos, el 36% de las escuelas primarias y el 20% de las escuelas de segundo ciclo de secundaria disponían de electricidad, pero no de Internet. Aun cuando las escuelas están conectadas a una fuente de energía, las subidas y bajas de tensión son comunes. La conectividad a Internet depende de la infraestructura nacional de telecomunicaciones, la geografía y la capacidad de las escuelas para pagar por el servicio."]
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "ELECTRIC"
          })
        }), _jsx(ScrollySection, {
          id: "LKA",
          children: _jsxs(_components.p, {
            children: ["Au ", _jsx(_components.strong, {
              children: "Sri Lanka"
            }), ", a pesar de que todas las escuelas primarias tienen electricidad, solo el 41% tiene acceso a Internet. Lo mismo ocurre con el 42% de las escuelas en Turkménistan."]
          })
        }), _jsx(ScrollySection, {
          id: "ECU,URY",
          children: _jsxs(_components.p, {
            children: ["En las escuelas de muchos países de América Latina no hay ni electricidad ni Internet. Únicamente alrededor del 60% de las escuelas del ", _jsx(_components.strong, {
              children: "Ecuador"
            }), " y 65% en Argentine disponen de acceso a ambos. Una excepción es el ", _jsx(_components.strong, {
              children: "Uruguay"
            }), ", donde el 100% de las escuelas primarias tiene acceso a los dos servicios."]
          })
        })]
      })]
    }), "\n", _jsx(_components.h2, {
      id: "violencia",
      children: "Violencia"
    }), "\n", _jsx(_components.p, {
      children: "Los actos de violencia, o las amenazas, relacionados con la escuela pueden ser de tipo psicológico, físico o sexual, y producirse tanto en locales escolares, como en el camino de la escuela, en el hogar o en el ciberespacio. A menudo se producen debido a una desigualdad de las dinámicas de poder, y son en muchos casos el resultado de normas negativas y estereotipos de género."
    }), "\n", _jsx(_components.p, {
      children: "Si bien la atención suele centrarse en los casos extremos, como los tiroteos, las formas de violencia más comunes y con frecuencia inadvertidas, como el matonismo, son las que tienen las mayores repercusiones negativas en la educación de los niños y adolescentes. Los datos sobre estas cuestiones siguen siendo insuficientes, porque las preguntas que se formulan para la recopilación de datos emplean distintas definiciones de comportamiento violento, los calendarios para la presentación de información no son uniformes, y falta coherencia en cuanto a las opciones de respuesta, las disposiciones en materia de confidencialidad o los protocolos de deontología. Esto se suma a que no se denuncien ciertas formas de violencia, como el acoso homofóbico, aún rodeadas de tabúes."
    }), "\n", _jsx(QualityFramework, {
      highlighted: "school",
      description: "Más información sobre el marco para la calidad de la educación"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.a.2",
      children: [_jsxs(_components.p, {
        children: ["El ", _jsx(_components.strong, {
          children: "matonismo"
        }), " es la forma de violencia más ampliamente documentada en las escuelas. Puede consistir en violencia física, abusos verbales o la intención de provocar daños psicológicos mediante la humillación o la exclusión. El siguiente gráfico muestra que en la mayoría de países al menos uno de cada tres adolescentes, con frecuencia más los chicos que las chicas, son víctimas del matonismo."]
      }), _jsx(Figure, {
        id: "BULLY_FIGHT"
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["La ", _jsx(_components.strong, {
        children: "violencia física"
      }), " comprende la intervención en peleas físicas en la escuela, portar un arma o ser amenazado con alguna, como una pistola, un cuchillo o un palo, en el recinto escolar, pertenecer o estar vinculado a una pandilla, o sufrir violencia física a manos de un miembro del personal."]
    }), "\n", _jsx(_components.p, {
      children: "Al menos el 10% de los chicos de 15 años de 24 países declararon haber participado en peleas físicas 3 veces o más en 2022, y la prevalencia alcanzó más del 30% en Armenia."
    }), "\n", _jsx(_components.h2, {
      id: "conclusión",
      children: "Conclusión"
    }), "\n", _jsx(_components.p, {
      children: "Los datos comparables en el plano internacional sobre los indicadores relativos a los docentes son sorprendentemente escasos. Relativamente pocos países proporcionan siquiera información sobre un recuento básico de los docentes, y eso no tiene en cuenta las horas de enseñanza, los docentes que ocupan puestos administrativos, y otras cuestiones complejas. Se necesita una tipología de las normas de capacitación de los docentes, para que así podamos detectar adecuadamente las lagunas, y llevar esta cuestión al plano mundial."
    }), "\n", _jsx(_components.p, {
      children: "Contar con instalaciones educativas “que tengan en cuenta las necesidades de los niños y las personas con discapacidad y las diferencias de género” y que ofrezcan “entornos de aprendizaje seguros, no violentos, inclusivos y eficaces para todos” es problemático en numerosos países, tal como demuestra este artículo. Al mismo tiempo, resulta difícil medir el progreso referente a esta cuestión, porque el indicador mundial pertinente no es una sola medida, sino un conjunto de varias dimensiones."
    }), "\n", _jsx(RelatedMaterial, {
      children: _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.oecd.org/en/publications/education-at-a-glance-2025_1c0d9c79-en.html",
            children: "Education at a glance – OECD"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://theirworld.org/news/safe-schools-report-reveals-crises-challenges-action-needed-to-educate-children",
            children: "Safe Schools Report – Their World"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://unesdoc.unesco.org/ark:/48223/pf0000232107",
            children: "School-related gender-based violence is preventing the achievement of quality education for all"
          })
        }), "\n"]
      })
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
